import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  GET_ALL_FILTER_TYPES_QUERY,
  GET_MY_AMCR_ACCOUNT_QUERY,
  GetAmcrFindPreviewsQueryName,
  GetMyFindQueryName,
  UPDATE_PAS_FIND_MUTATION,
  reconcileFindsInCache,
} from '../../gql';
import { VocabularyKind, useVocabulary } from '../../i18n';
import { Button } from '../design-system';
import {
  FIND_PAS_EDIT_FORM_ID,
  FindPasEditForm,
  FindPasInput,
  FindPasOptions,
} from './FindPasEditForm';
import { findPasEditMessages } from './messages';
import { PasFind } from './pasFind';

export * from './pasFind';

interface FindPasEditDialogProps {
  find: PasFind;
  onClose: () => void;
  onSaved: () => void;
}

interface FilterTypes {
  eras?: { id: string; parentId?: string | null }[];
  findTypes?: { id: string; category?: string | null }[];
  materials?: string[];
}

interface SaveError {
  error: string;
  path?: string;
}

type Vocabulary = (kind: VocabularyKind, value?: string | null) => string;

/** A parent era with no children, so the child-only era list omits it. */
const INDETERMINATE_DATING = 'indeterminate dating';

const NO_FINDER_NAMES: string[] = [];

const contentStyles = { minWidth: { sm: 560 } };
const loadingStyles = { py: 4, alignItems: 'center' };
const errorListStyles = { m: 0, pl: 2 };

const toFindPasOptions = (
  filterTypes: FilterTypes | undefined,
  vocabulary: Vocabulary
): FindPasOptions => {
  const eras = [
    ...(filterTypes?.eras ?? []).filter((era) => era.parentId),
    { id: INDETERMINATE_DATING, parentId: INDETERMINATE_DATING },
  ];

  return {
    types: filterTypes?.findTypes ?? [],
    materials: filterTypes?.materials ?? [],
    eras: eras.map((era) => ({
      id: era.id.toLocaleLowerCase(),
      label: vocabulary('era', era.id),
      parentLabel: vocabulary('era', era.parentId),
    })),
  };
};

/** Quick edit of the Acolyte values that a PAS record is created from. */
const FindPasEditDialog = ({
  find,
  onClose,
  onSaved,
}: FindPasEditDialogProps) => {
  const intl = useIntl();
  const vocabulary = useVocabulary();
  const [saveErrors, setSaveErrors] = useState<SaveError[]>([]);

  const filterTypesQuery = useQuery(GET_ALL_FILTER_TYPES_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const filterTypes: FilterTypes | undefined =
    filterTypesQuery.data?.getAllFilterTypes;
  const options = useMemo(() => toFindPasOptions(filterTypes, vocabulary), [
    filterTypes,
    vocabulary,
  ]);

  const accountQuery = useQuery(GET_MY_AMCR_ACCOUNT_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const finderNames: string[] =
    accountQuery.data?.getMyAmcrAccount?.finderNames ?? NO_FINDER_NAMES;

  const [updatePasFind, { loading: saving }] = useMutation(
    UPDATE_PAS_FIND_MUTATION,
    {
      refetchQueries: [GetAmcrFindPreviewsQueryName, GetMyFindQueryName],
      update: (cache, { data }) => {
        const updatedFind = data?.updateFind?.find;

        if (updatedFind) {
          reconcileFindsInCache(cache, [updatedFind.id]);
        }
      },
    }
  );

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const handleSubmit = async (input: FindPasInput) => {
    setSaveErrors([]);

    try {
      const { data } = await updatePasFind({
        variables: {
          id: find.id,
          find: { ...input, visibility: find.visibility ?? 'private' },
        },
      });

      if (data?.updateFind?.find) {
        onSaved();
        onClose();
        return;
      }

      setSaveErrors(data?.updateFind?.errors || []);
    } catch {
      setSaveErrors([
        { error: intl.formatMessage(findPasEditMessages.failed) },
      ]);
    }
  };

  const isLoading = filterTypesQuery.loading && !filterTypes;

  return (
    <Dialog open maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage
          id="findPasEdit.title"
          defaultMessage="Edit find for PAS"
        />
      </DialogTitle>
      <DialogContent sx={contentStyles}>
        {isLoading ? (
          <Stack sx={loadingStyles}>
            <CircularProgress size={32} />
          </Stack>
        ) : (
          <Stack gap={2}>
            <FindPasEditForm
              find={find}
              options={options}
              finderNames={finderNames}
              disabled={saving}
              onSubmit={handleSubmit}
            />
            {filterTypesQuery.error && (
              <Alert severity="error" variant="outlined">
                <FormattedMessage
                  id="findPasEdit.optionsFailed"
                  defaultMessage="The find options could not be loaded."
                />
              </Alert>
            )}
            {saveErrors.length > 0 && (
              <Alert severity="error" variant="outlined">
                <Stack component="ul" sx={errorListStyles}>
                  {saveErrors.map(({ error, path }) => (
                    <li key={`${path}:${error}`}>{error}</li>
                  ))}
                </Stack>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button tone="neutral" disabled={saving} onClick={handleClose}>
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          type="submit"
          form={FIND_PAS_EDIT_FORM_ID}
          disabled={saving || isLoading}
        >
          <FormattedMessage id="general.save" defaultMessage="Save" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { FindPasEditDialog };
